import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toCSV, downloadCSV, inDateRange } from "@/lib/csv";
import { PageHeader, Card, Empty } from "@/components/UI";

const weekKey = (dateStr) => {
  const d = new Date(dateStr);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};

// Convert pcs → "d.pp" hybrid string (e.g. 131 → "10.11"). Matches CuttingPage / DCPage rules.
const pcsToHybridStr = (pcs) => {
  const n = Math.max(0, parseInt(pcs, 10) || 0);
  const doz = Math.floor(n / 12);
  const rem = n % 12;
  return rem === 0 ? String(doz) : `${doz}.${String(rem).padStart(2, "0")}`;
};

export default function ReportsPage() {
  const [cutting, setCutting] = useState([]);
  const [stitching, setStitching] = useState([]);
  const [packing, setPacking] = useState([]);
  const [stock, setStock] = useState([]);
  const [rejection, setRejection] = useState(null);
  const [pendingStitch, setPendingStitch] = useState([]);
  // "line" = granular (per DC line) · "product" = rolled up by product
  const [pendingView, setPendingView] = useState("product");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tab, setTab] = useState("cutting");

  useEffect(() => {
    api.get("/cutting").then((r) => setCutting(r.data)).catch(() => {});
    api.get("/stitching").then((r) => setStitching(r.data)).catch(() => {});
    api.get("/packing").then((r) => setPacking(r.data)).catch(() => {});
    api.get("/stock").then((r) => setStock(r.data)).catch(() => {});
    api.get("/reports/rejection").then((r) => setRejection(r.data)).catch(() => {});
    api.get("/reports/pending-stitching").then((r) => setPendingStitch(r.data)).catch(() => {});
  }, []);

  const build = (rows, valueKey) => {
    const map = {};
    rows.filter((r) => inDateRange(r.date, from, to)).forEach((r) => {
      const worker = r.worker_name || "Unassigned";
      const wk = weekKey(r.date);
      const k = `${worker}|${wk}`;
      if (!map[k]) map[k] = { worker, week: wk, jobs: 0, total: 0 };
      map[k].jobs += 1;
      map[k].total += Number(r[valueKey]) || 0;
    });
    return Object.values(map).sort((a, b) => b.week.localeCompare(a.week) || b.total - a.total);
  };

  const cuttingReport = useMemo(() => build(cutting, "pcs_out"), [cutting, from, to]);
  const stitchingReport = useMemo(() => build(stitching, "pcs"), [stitching, from, to]);
  const packingReport = useMemo(() => build(packing, "pcs"), [packing, from, to]);

  // Total pcs stitched per product (both inhouse + outsourced).
  // Group by style_name + size + color (SKU excluded) so hash-only duplicates collapse into one row.
  const stitchByProduct = useMemo(() => {
    const productMeta = {}; // pid -> {style_name, size, color, sku}
    (stock || []).forEach((s) => { productMeta[s.id] = s; });
    const map = {};
    stitching.filter((r) => inDateRange(r.date, from, to)).forEach((r) => {
      const meta = productMeta[r.product_id] || {};
      // Compose the stable label without SKU
      const stable = [meta.style_name || "", meta.size || "", meta.color || ""].filter(Boolean).join(" · ") || (r.product_label || "—");
      if (!map[stable]) map[stable] = { product_label: stable, inhouse: 0, outsourced: 0, jobs: 0, total: 0, sku_variants: new Set() };
      const pcs = Number(r.pcs) || 0;
      if (r.mode === "outsourced") map[stable].outsourced += pcs;
      else map[stable].inhouse += pcs;
      map[stable].jobs += 1;
      map[stable].total += pcs;
      if (meta.sku) map[stable].sku_variants.add(meta.sku);
    });
    return Object.values(map)
      .map((r) => ({ ...r, sku_variants: r.sku_variants.size }))
      .sort((a, b) => b.total - a.total);
  }, [stitching, stock, from, to]);

  // Stitching WIP per product — one row per SKU (matches Stock & Ready-for-Packing).
  // Filter out rows with zero cut AND zero stitched. Keep the "SKU codes" hint when
  // multiple product_ids share the same style/size/color (data-hygiene signal only).
  const stitchWipByProduct = useMemo(() => {
    const rows = (stock || [])
      .filter((s) => (s.cut_pcs || 0) > 0 || (s.stitched_pcs || 0) > 0)
      .map((s) => {
        const label = [s.style_name || "", s.sku || "", s.size || "", s.color || ""]
          .filter(Boolean)
          .join(" / ") || "—";
        const cut = s.cut_pcs || 0;
        const stitched = s.stitched_pcs || 0;
        return {
          product_id: s.id,
          product_label: label,
          style_key: [s.style_name || "", s.size || "", s.color || ""].join("|"),
          cut,
          stitched,
          wip: Math.max(0, cut - stitched),
        };
      });
    // Annotate rows that share the same style/size/color (siblings) so the user can
    // spot legacy hash duplicates at a glance.
    const styleCounts = rows.reduce((acc, r) => { acc[r.style_key] = (acc[r.style_key] || 0) + 1; return acc; }, {});
    return rows
      .map((r) => ({ ...r, sku_variants: styleCounts[r.style_key] || 1 }))
      .sort((a, b) => (b.wip - a.wip) || (b.cut - a.cut) || a.product_label.localeCompare(b.product_label));
  }, [stock]);

  // Pending Stitching by Product — rolls up granular DC lines to one row per SKU.
  const pendingByProduct = useMemo(() => {
    const map = {};
    (pendingStitch || []).forEach((r) => {
      const key = r.product_id || `_unlinked_${r.product_label}`;
      if (!map[key]) {
        map[key] = {
          product_id: r.product_id,
          product_label: r.product_label,
          dcs: 0,
          stitchers: new Set(),
          sent_pcs: 0,
          received_pcs: 0,
          pending_pcs: 0,
        };
      }
      map[key].dcs += 1;
      if (r.party_id) map[key].stitchers.add(r.party_id);
      map[key].sent_pcs += r.sent_pcs;
      map[key].received_pcs += r.received_pcs;
      map[key].pending_pcs += r.pending_pcs;
    });
    return Object.values(map)
      .map((r) => ({ ...r, stitchers: r.stitchers.size }))
      .sort((a, b) => b.pending_pcs - a.pending_pcs);
  }, [pendingStitch]);

  const active = tab === "cutting" ? cuttingReport
    : tab === "stitching" ? stitchingReport
    : tab === "packing" ? packingReport
    : tab === "stitch-by-product" ? stitchByProduct
    : stitchWipByProduct;
  const label = tab === "cutting" ? "Pcs Cut" : tab === "stitching" ? "Pcs Stitched" : tab === "packing" ? "Pcs Packed" : "Total Stitched";

  const exportCSV = () => {
    if (tab === "rejection") {
      const rej = rejection || { by_stitcher: [], by_sku: [] };
      const cols = [
        { key: "name", label: "Name" },
        { key: "kind", label: "Type" },
        { key: "jobs", label: "Jobs" },
        { key: "good_pcs", label: "Good Pcs" },
        { key: "mistake_pcs", label: "Mistake Pcs" },
        { key: "rejection_pct", label: "Rejection %" },
      ];
      const rows = [
        ...(rej.by_stitcher || []).map((r) => ({ name: r.party_name, kind: "Stitcher", jobs: r.jobs, good_pcs: r.good_pcs, mistake_pcs: r.mistake_pcs, rejection_pct: r.rejection_pct })),
        ...(rej.by_sku || []).map((r) => ({ name: r.product_label, kind: "SKU", jobs: r.jobs, good_pcs: r.good_pcs, mistake_pcs: r.mistake_pcs, rejection_pct: r.rejection_pct })),
      ];
      downloadCSV(`slkc-rejection-report.csv`, toCSV(rows, cols));
      return;
    }
    if (tab === "pending-stitching") {
      if (pendingView === "product") {
        const cols = [
          { key: "product_label", label: "Product" },
          { key: "stitchers", label: "Stitchers" },
          { key: "dcs", label: "Open DCs" },
          { key: "sent_pcs", label: "Sent (Pcs)" },
          { key: "received_pcs", label: "Received (Pcs)" },
          { key: "pending_pcs", label: "Pending (Pcs)" },
          { key: "pending_hybrid", label: "Pending (Doz.Pcs)" },
        ];
        const rows = pendingByProduct.map((r) => ({ ...r, pending_hybrid: pcsToHybridStr(r.pending_pcs) }));
        downloadCSV(`slkc-pending-stitching-by-product.csv`, toCSV(rows, cols));
      } else {
        const cols = [
          { key: "dc_date", label: "DC Date" },
          { key: "dc_number", label: "DC #" },
          { key: "party_name", label: "Stitcher" },
          { key: "product_label", label: "Product" },
          { key: "lot_no", label: "Lot #" },
          { key: "sent_pcs", label: "Sent (Pcs)" },
          { key: "received_pcs", label: "Received (Pcs)" },
          { key: "pending_pcs", label: "Pending (Pcs)" },
          { key: "pending_hybrid", label: "Pending (Doz.Pcs)" },
        ];
        const rows = (pendingStitch || []).map((r) => ({ ...r, pending_hybrid: pcsToHybridStr(r.pending_pcs) }));
        downloadCSV(`slkc-pending-stitching-by-line.csv`, toCSV(rows, cols));
      }
      return;
    }
    if (tab === "stitch-by-product") {
      const cols = [
        { key: "product_label", label: "Product" },
        { key: "jobs", label: "Jobs" },
        { key: "inhouse", label: "Inhouse Pcs" },
        { key: "outsourced", label: "Outsourced Pcs" },
        { key: "total", label: "Total Stitched" },
      ];
      downloadCSV(`slkc-stitch-by-product-${from || "all"}-${to || "today"}.csv`, toCSV(active, cols));
      return;
    }
    if (tab === "stitch-wip-by-product") {
      const cols = [
        { key: "product_label", label: "Product" },
        { key: "cut", label: "Cut" },
        { key: "stitched", label: "Stitched" },
        { key: "wip", label: "Stitching WIP" },
      ];
      downloadCSV(`slkc-stitch-wip-by-product.csv`, toCSV(active, cols));
      return;
    }
    const cols = [
      { key: "week", label: "Week" },
      { key: "worker", label: "Worker" },
      { key: "jobs", label: "Jobs" },
      { key: "total", label: label },
    ];
    downloadCSV(`slkc-${tab}-weekly-${from || "all"}-${to || "today"}.csv`, toCSV(active, cols));
  };

  return (
    <div>
      <PageHeader
        label="Reports"
        title="Weekly Output by Worker"
        subtitle="Cutting · Stitching · Packing — grouped by ISO week & person"
        right={
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="reports-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="reports-to" />
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-4 pb-3 flex gap-2 flex-wrap sticky top-0 z-20 bg-background border-b border-border">
        {["cutting", "stitching", "packing", "stitch-by-product", "stitch-wip-by-product", "pending-stitching", "rejection"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`reports-tab-${t}`}
            className={`h-8 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold ${tab === t ? "bg-foreground text-background" : "bg-white border border-border"}`}
          >
            {t === "stitch-by-product" ? "Stitched · By Product" : t === "stitch-wip-by-product" ? "Stitch WIP · By Product" : t === "pending-stitching" ? "Pending Stitching DCs" : t === "rejection" ? "Rejection Report" : t}
          </button>
        ))}
      </div>

      <div className="p-4 md:p-8">
        <Card
          title={
            tab === "rejection" ? `Rejection Report · overall ${rejection?.overall_rejection_pct ?? 0}% · ${rejection?.total_mistake_pcs ?? 0} of ${(rejection?.total_good_pcs ?? 0) + (rejection?.total_mistake_pcs ?? 0)} pcs` :
            tab === "pending-stitching" ? (() => {
              const totalPending = (pendingStitch || []).reduce((s, r) => s + r.pending_pcs, 0);
              const openDcs = new Set((pendingStitch || []).map((r) => r.dc_id)).size;
              return `Pending Stitching DCs · ${openDcs} open DC${openDcs === 1 ? "" : "s"} · ${totalPending} pcs pending (${pcsToHybridStr(totalPending)} doz.pcs)`;
            })() :
            tab === "stitch-by-product" ? `Total Stitched by Product · ${active.length} SKUs · scroll ↕ for all` :
            tab === "stitch-wip-by-product" ? `Stitching WIP by Product · ${active.length} SKUs · scroll ↕ for all` :
            `${tab.charAt(0).toUpperCase() + tab.slice(1)} · ${active.length} rows`
          }
          right={
            <div className="flex items-center gap-2">
              {tab === "pending-stitching" && (
                <div className="flex gap-1" data-testid="pending-view-toggle">
                  {["product", "line"].map((v) => (
                    <button
                      key={v}
                      onClick={() => setPendingView(v)}
                      data-testid={`pending-view-${v}`}
                      className={`h-8 px-3 font-mono text-[10px] tracking-[0.15em] uppercase font-bold ${pendingView === v ? "bg-foreground text-background" : "bg-white border border-border"}`}
                    >
                      {v === "product" ? "By Product" : "By Line"}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={exportCSV} data-testid="reports-csv" className="h-8 px-3 font-mono text-[10px] tracking-[0.15em] uppercase font-bold bg-foreground text-background">
                ↓ CSV
              </button>
            </div>
          }
        >
          <div
            className="overflow-y-scroll overflow-x-auto"
            style={{
              maxHeight: "calc(100dvh - 220px)",
              scrollbarWidth: "auto",
              scrollbarColor: "#64748b transparent",
            }}
            data-testid="reports-scroll"
          >
            {tab === "rejection" ? (
              <div className="space-y-6">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">By Stitcher</div>
                  <table className="table-dense w-full">
                    <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                      <tr>
                        <th>Stitcher</th>
                        <th className="text-right">Jobs</th>
                        <th className="text-right">Good Pcs</th>
                        <th className="text-right">Mistake Pcs</th>
                        <th className="text-right">Rejection %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rejection?.by_stitcher || []).map((r, i) => (
                        <tr key={i} data-testid={`rejection-stitcher-row-${i}`}>
                          <td className="font-semibold">{r.party_name}</td>
                          <td className="tabular text-right">{r.jobs}</td>
                          <td className="tabular text-right">{r.good_pcs}</td>
                          <td className="tabular text-right font-bold text-destructive">{r.mistake_pcs}</td>
                          <td className={`tabular text-right font-bold ${r.rejection_pct > 5 ? "text-destructive" : r.rejection_pct > 2 ? "text-accent" : "text-muted-foreground"}`}>{r.rejection_pct}%</td>
                        </tr>
                      ))}
                      {(!rejection?.by_stitcher || rejection.by_stitcher.length === 0) && (
                        <tr><td colSpan={5}><Empty text="No stitching receipts with mistake pcs yet" /></td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">By SKU</div>
                  <table className="table-dense w-full">
                    <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                      <tr>
                        <th>Product</th>
                        <th className="text-right">Jobs</th>
                        <th className="text-right">Good Pcs</th>
                        <th className="text-right">Mistake Pcs</th>
                        <th className="text-right">Rejection %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rejection?.by_sku || []).map((r, i) => (
                        <tr key={i} data-testid={`rejection-sku-row-${i}`}>
                          <td>{r.product_label}</td>
                          <td className="tabular text-right">{r.jobs}</td>
                          <td className="tabular text-right">{r.good_pcs}</td>
                          <td className="tabular text-right font-bold text-destructive">{r.mistake_pcs}</td>
                          <td className={`tabular text-right font-bold ${r.rejection_pct > 5 ? "text-destructive" : r.rejection_pct > 2 ? "text-accent" : "text-muted-foreground"}`}>{r.rejection_pct}%</td>
                        </tr>
                      ))}
                      {(!rejection?.by_sku || rejection.by_sku.length === 0) && (
                        <tr><td colSpan={5}><Empty text="No mistake pcs recorded on any SKU yet" /></td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : tab === "pending-stitching" ? (
              pendingView === "product" ? (
                <table className="table-dense w-full">
                  <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                    <tr>
                      <th>Product</th>
                      <th className="text-right">Stitchers</th>
                      <th className="text-right">Open DCs</th>
                      <th className="text-right">Sent</th>
                      <th className="text-right">Received</th>
                      <th className="text-right">Pending (Pcs)</th>
                      <th className="text-right">Pending (Doz.Pcs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingByProduct.map((r, i) => (
                      <tr key={r.product_id || i} data-testid={`pending-product-row-${i}`}>
                        <td>{r.product_label}</td>
                        <td className="tabular text-right">{r.stitchers}</td>
                        <td className="tabular text-right">{r.dcs}</td>
                        <td className="tabular text-right">{r.sent_pcs}</td>
                        <td className="tabular text-right">{r.received_pcs}</td>
                        <td className="tabular text-right font-bold text-destructive">{r.pending_pcs}</td>
                        <td className="tabular text-right font-mono">{pcsToHybridStr(r.pending_pcs)}</td>
                      </tr>
                    ))}
                    {pendingByProduct.length === 0 && (
                      <tr><td colSpan={7}><Empty text="No pending stitching — every DC is fully received" /></td></tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="table-dense w-full">
                  <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                    <tr>
                      <th>DC Date</th>
                      <th>DC #</th>
                      <th>Stitcher</th>
                      <th>Product</th>
                      <th>Lot #</th>
                      <th className="text-right">Sent</th>
                      <th className="text-right">Received</th>
                      <th className="text-right">Pending (Pcs)</th>
                      <th className="text-right">Pending (Doz.Pcs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pendingStitch || []).map((r, i) => (
                      <tr key={`${r.dc_id}-${i}`} data-testid={`pending-line-row-${i}`}>
                        <td className="font-mono">{r.dc_date}</td>
                        <td className="font-mono text-xs">{r.dc_number}</td>
                        <td>{r.party_name}</td>
                        <td>{r.product_label}</td>
                        <td className="font-mono text-xs text-muted-foreground">{r.lot_no || "—"}</td>
                        <td className="tabular text-right">{r.sent_pcs}</td>
                        <td className="tabular text-right">{r.received_pcs}</td>
                        <td className="tabular text-right font-bold text-destructive">{r.pending_pcs}</td>
                        <td className="tabular text-right font-mono">{pcsToHybridStr(r.pending_pcs)}</td>
                      </tr>
                    ))}
                    {(pendingStitch || []).length === 0 && (
                      <tr><td colSpan={9}><Empty text="No pending stitching lines" /></td></tr>
                    )}
                  </tbody>
                </table>
              )
            ) : tab === "stitch-wip-by-product" ? (
              <table className="table-dense w-full">
                <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                  <tr>
                    <th>Product</th>
                    <th className="text-right">Cut</th>
                    <th className="text-right">Stitched</th>
                    <th className="text-right">WIP (Pending)</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((r, i) => (
                    <tr key={r.product_id || i} data-testid={`stitch-wip-row-${i}`}>
                      <td>
                        {r.product_label}
                        {r.sku_variants > 1 && <span className="ml-2 text-[10px] text-muted-foreground">({r.sku_variants} SKUs share this style/size/color)</span>}
                      </td>
                      <td className="tabular text-right">{r.cut}</td>
                      <td className="tabular text-right">{r.stitched}</td>
                      <td className={`tabular text-right font-bold ${r.wip > 0 ? "text-destructive" : "text-muted-foreground"}`}>{r.wip}</td>
                    </tr>
                  ))}
                  {active.length === 0 && (
                    <tr><td colSpan={4}><Empty text="No stitching activity yet" /></td></tr>
                  )}
                </tbody>
              </table>
            ) : tab === "stitch-by-product" ? (
              <table className="table-dense w-full">
                <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                  <tr>
                    <th>Product</th>
                    <th className="text-right">Jobs</th>
                    <th className="text-right">Inhouse</th>
                    <th className="text-right">Outsourced</th>
                    <th className="text-right">Total Stitched</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((r, i) => (
                    <tr key={i} data-testid={`stitch-product-row-${i}`}>
                      <td>
                        {r.product_label}
                        {r.sku_variants > 1 && <span className="ml-2 text-[10px] text-muted-foreground">({r.sku_variants} SKU codes)</span>}
                      </td>
                      <td className="tabular text-right">{r.jobs}</td>
                      <td className="tabular text-right">{r.inhouse}</td>
                      <td className="tabular text-right">{r.outsourced}</td>
                      <td className="tabular text-right font-bold">{r.total}</td>
                    </tr>
                  ))}
                  {active.length === 0 && (
                    <tr><td colSpan={5}><Empty text="No stitching entries in this date range" /></td></tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="table-dense w-full">
                <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                  <tr>
                    <th>Week</th>
                    <th>Worker</th>
                    <th className="text-right">Jobs</th>
                    <th className="text-right">{label}</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((r) => (
                    <tr key={`${r.worker}-${r.week}`}>
                      <td className="font-mono">{r.week}</td>
                      <td className="font-semibold">{r.worker}</td>
                      <td className="tabular text-right">{r.jobs}</td>
                      <td className="tabular text-right font-bold">{r.total}</td>
                    </tr>
                  ))}
                  {active.length === 0 && (
                    <tr>
                      <td colSpan={4}>
                        <Empty text="No data — record entries with a worker to see the report" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
